/**
 * The composer's text field with mention chips: the draft stays the token string (`@Fizz`, `<@id>`, `#design`,
 * `@here`), shown with each token the server would resolve as one atomic chip (core logic/composerText). Built on
 * RichTextField (ProseMirror, loaded lazily; the plain TextField is shown until it loads, so first paint and early
 * typing don't wait for it). Same contract as the TextField it replaces: value / onChange, caret reports and requests
 * in draft offsets, the autocomplete keys, Enter sends, Shift+Enter breaks the line, pasted files.
 */
import { ComposerFormat, ComposerText, type ComposerChipContext, type ComposerSegment, type FormatKind, type PickedFile } from "@bundle/core";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { TextField, type TextFieldKey } from "../primitives/index.ts";
import { preloadRichTextField, RichTextField, type RichRun, type RichTextKeyEvent } from "../primitives/RichTextField.tsx";

/** The composer's formatting toolbar calls this with a kind; it formats the field's current selection. */
export type ComposerFormatter = (kind: FormatKind) => void;

export type ComposerFieldProps = {
  value: string;
  onChange: (text: string) => void;
  onSubmit: () => void;
  /** Who and what tokens resolve to (the workspace directory and me, for the "self" highlight). */
  chips: ComposerChipContext;
  placeholder: string;
  autoFocus?: boolean;
  onPasteFiles?: (files: PickedFile[]) => void;
  onSelectionChange?: (start: number, end: number) => void;
  onKey?: (key: TextFieldKey) => boolean;
  selection?: { start: number; end: number; seq: number };
  /** Filled with the formatter (Slack's toolbar buttons) while the rich field is up; null otherwise. */
  formatRef?: { current: ComposerFormatter | null };
  testID: string;
};

/** Typing within this long of the last edit is one undo step (until a space or a line break). */
const UNDO_COALESCE_MS = 1000;
const UNDO_MAX = 100;

type Snapshot = { text: string; caret: number };

function runsOf(segments: ComposerSegment[]): RichRun[] {
  return segments.map((s): RichRun => {
    if (s.type === "text") return { text: s.text };
    // A user group chip behaves like a broadcast one (no person behind it).
    const kind = s.chip === "channel" ? "channel" : s.chip === "broadcast" || s.groupId !== undefined ? "broadcast" : "user";
    const id = s.userId ?? s.channelId ?? s.range ?? s.groupId;
    return { text: ComposerText.CHIP_CHAR, mention: { kind, ...(id === undefined ? {} : { id }), label: s.label, chip: s.chip, copyText: s.token } };
  });
}

export function ComposerField(p: ComposerFieldProps) {
  useEffect(preloadRichTextField, []);
  const segments = useMemo(() => ComposerText.composerSegments(p.value, p.chips), [p.value, p.chips]);
  const runs = useMemo(() => runsOf(segments), [segments]);
  const latest = useRef({ p, segments });
  useLayoutEffect(() => {
    latest.current = { p, segments };
  });
  /** Set while the field reports its own edit: the selection report that follows belongs to the new text. */
  const fieldEdit = useRef(false);
  const undo = useRef<{ past: Snapshot[]; future: Snapshot[]; at: number }>({ past: [], future: [], at: 0 });
  /** Caret requests of our own (an edit made here rather than by the field, undo); `vmSeq` = the view model's seq then. */
  const [local, setLocal] = useState<{ caret: number; end?: number; seq: number; vmSeq: number | undefined }>();
  /** The field's selection (view offsets), for the toolbar's buttons. */
  const lastSelection = useRef({ start: 0, end: 0 });

  const commit = (text: string, caret: number, from: "field" | "here", selEnd?: number) => {
    const cur = latest.current;
    if (from === "here") setLocal((f) => ({ caret, ...(selEnd === undefined ? {} : { end: selEnd }), seq: (f?.seq ?? 0) + 1, vmSeq: cur.p.selection?.seq }));
    else fieldEdit.current = true;
    cur.p.onChange(text);
    cur.p.onSelectionChange?.(caret, selEnd ?? caret);
  };

  /** Slack's formatting (toolbar, ⌘B …) on the view selection [start, end): markdown added or removed (logic/composerFormat). */
  const format = (kind: FormatKind, start: number, end: number) => {
    const cur = latest.current;
    const from = ComposerText.toTextOffset(cur.segments, Math.min(start, end));
    const to = ComposerText.toTextOffset(cur.segments, Math.max(start, end));
    const edit = ComposerFormat.applyFormat(cur.p.value, from, to, kind);
    const h = undo.current;
    h.past = [...h.past, { text: cur.p.value, caret: to }].slice(-UNDO_MAX);
    h.future = [];
    h.at = 0;
    commit(edit.text, edit.start, "here", edit.end === edit.start ? undefined : edit.end);
  };
  useEffect(() => {
    const ref = p.formatRef;
    if (ref === undefined) return undefined;
    ref.current = (kind) => format(kind, lastSelection.current.start, lastSelection.current.end);
    return () => {
      ref.current = null;
    };
  });

  /** [start, end) of the field's text → `inserted`, recorded for undo (typing coalesces into one step). */
  const replace = (start: number, end: number, inserted: string, from: "field" | "here") => {
    const cur = latest.current;
    const edit = ComposerText.applyViewEdit(cur.p.value, cur.segments, start, end, inserted);
    const h = undo.current;
    const now = Date.now();
    const typing = start === end && inserted.length === 1 && !/\s/u.test(inserted);
    if (!(typing && now - h.at < UNDO_COALESCE_MS && h.past.length > 0)) {
      h.past = [...h.past, { text: cur.p.value, caret: ComposerText.toTextOffset(cur.segments, Math.max(start, end)) }].slice(-UNDO_MAX);
    }
    h.future = [];
    h.at = typing ? now : 0;
    commit(edit.text, edit.caret, from);
  };

  const step = (from: "past" | "future") => {
    const h = undo.current;
    const target = h[from].at(-1);
    if (target === undefined) return;
    const cur = latest.current;
    const here: Snapshot = { text: cur.p.value, caret: cur.p.value.length };
    if (from === "past") {
      h.past = h.past.slice(0, -1);
      h.future = [...h.future, here];
    } else {
      h.future = h.future.slice(0, -1);
      h.past = [...h.past, here];
    }
    h.at = 0;
    commit(target.text, Math.min(target.caret, target.text.length), "here");
  };

  const onKey = (e: RichTextKeyEvent): boolean => {
    const cur = latest.current;
    switch (e.key) {
      case "up":
      case "down":
      case "tab":
      case "escape":
      case "enter":
        if (cur.p.onKey?.(e.key) === true) return true;
        if (e.key !== "enter") return false;
        cur.p.onSubmit();
        return true;
      case "shiftEnter":
        replace(e.start, e.end, "\n", "here");
        return true;
      case "backspace":
      case "delete": {
        // A chip goes as a whole (its token is one character here).
        if (e.start !== e.end) return false;
        const at = e.key === "backspace" ? e.start - 1 : e.start;
        if (ComposerText.viewText(cur.segments).charAt(at) !== ComposerText.CHIP_CHAR) return false;
        replace(at, at + 1, "", "here");
        return true;
      }
      case "undo":
      case "redo":
        step(e.key === "undo" ? "past" : "future");
        return true;
      case "bold":
      case "italic":
      case "strike":
      case "code":
      case "link":
        // Slack's ⌘B / ⌘I / ⌘⇧X / ⌘E / ⌘K: the draft is markdown, so these add (or remove) its syntax.
        format(e.key, e.start, e.end);
        return true;
      case "underline":
        // Slack has no underline; keep the browser from adding a mark.
        return true;
      default:
        return false;
    }
  };

  // autoFocus holds against a field that grabs the focus right after this one mounts (a thread pane's composer that
  // just opened, as the editor opened by ↑ there mounts): if it isn't here a moment later, take it back once.
  const autoFocus = p.autoFocus === true;
  useEffect(() => {
    if (!autoFocus) return undefined;
    const reclaim = () => {
      const active = document.activeElement;
      if (active !== null && active.closest(`[data-testid="${latest.current.p.testID}"]`) !== null) return;
      const vmSeqNow = latest.current.p.selection?.seq;
      setLocal((f) => ({ caret: latest.current.p.value.length, seq: (f?.seq ?? 0) + 1, vmSeq: vmSeqNow }));
    };
    const timer = setTimeout(reclaim, 150);
    return () => clearTimeout(timer);
  }, [autoFocus]);

  // Caret requests: the view model's (after a completion, the @ button) or ours; whichever came last. None on mount.
  const vmSeq = p.selection?.seq;
  const seq = (vmSeq ?? 0) + (local?.seq ?? 0);
  const [mountSeq] = useState(seq);
  const ownRequest = local !== undefined && local.vmSeq === vmSeq;
  const caret = ownRequest ? local.caret : p.selection?.end;
  const selEnd = ownRequest ? local.end : undefined;
  const focus =
    seq === mountSeq || caret === undefined
      ? undefined
      : { offset: ComposerText.toViewOffset(segments, caret), ...(selEnd === undefined ? {} : { end: ComposerText.toViewOffset(segments, selEnd) }), seq };
  // The field doesn't report selections it is told to make, so the toolbar would format where the selection was before
  // (after ⌘⇧9, a toolbar button, undo, a completion): remember the requested one.
  const focusSeq = focus?.seq;
  useLayoutEffect(() => {
    if (focus !== undefined) lastSelection.current = { start: focus.offset, end: focus.end ?? focus.offset };
    // Only a new request (seq); later renders keep what the field reports.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusSeq]);

  return (
    <RichTextField
      runs={runs}
      onChange={(start, end, inserted) => replace(start, end, inserted, "field")}
      onFormatKey={(key, start, end) => {
        format(key, start, end);
        return true;
      }}
      onSelect={(start, end) => {
        lastSelection.current = { start, end };
        const own = fieldEdit.current;
        fieldEdit.current = false;
        if (own) return;
        const cur = latest.current;
        cur.p.onSelectionChange?.(ComposerText.toTextOffset(cur.segments, start), ComposerText.toTextOffset(cur.segments, end));
      }}
      onKey={onKey}
      onPaste={(text, start, end) => {
        replace(start, end, text.replace(/\r\n?/g, "\n"), "here");
        return true;
      }}
      onPasteFiles={p.onPasteFiles}
      focus={focus}
      autoFocus={p.autoFocus}
      placeholder={p.placeholder}
      accessibilityLabel={p.placeholder}
      maxHeight="composer.maxHeight"
      fallback={
        <TextField
          variant="plain"
          multiline
          value={p.value}
          onChange={p.onChange}
          onSubmit={p.onSubmit}
          placeholder={p.placeholder}
          autoFocus={p.autoFocus}
          onPasteFiles={p.onPasteFiles}
          onSelectionChange={p.onSelectionChange}
          onKey={p.onKey}
          selection={p.selection}
          testID={p.testID}
        />
      }
      testID={p.testID}
    />
  );
}
